import { WhatsNewPage } from 'features/whats-new';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRightStraight, X16 } from 'shared/assets/icons';
import { DialogVariantOptions } from 'shared/assets/styles';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { IconButton } from 'shared/components/ui/icon-button';
import { changelogLink } from 'shared/constants';

import * as Styled from './whats-new-dialog.style';
import { WhatsNewSlide } from './whats-new-slide';

export type WhatsNewDialogProps = {
  open: boolean;
  onClose: () => void;
  pages: WhatsNewPage[];
};

export const WhatsNewDialog: React.FC<WhatsNewDialogProps> = ({
  open,
  onClose,
  pages,
}) => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);

  const hasMultiplePages = pages.length > 1;
  const isFirstPage = activeIndex === 0;
  const isLastPage = activeIndex >= pages.length - 1;
  const currentPage = pages[activeIndex];

  const handleClose = () => {
    setActiveIndex(0);
    onClose();
  };

  const handleNext = () => {
    if (isLastPage) {
      handleClose();
      return;
    }
    setActiveIndex((index) => index + 1);
  };

  const handleBack = () => setActiveIndex((index) => Math.max(0, index - 1));

  const handleSeeAllUpdates = () =>
    window.open(changelogLink, '_blank', 'noopener,noreferrer');

  if (!currentPage) {
    return null;
  }

  return (
    <Styled.Dialog
      open={open}
      fullWidth
      onClose={handleClose}
      variant={DialogVariantOptions.MediumSmall}
    >
      <Styled.Content>
        <Styled.CloseButtonContainer>
          <IconButton
            icon={X16}
            onClick={handleClose}
            aria-label={t('general.close')}
          />
        </Styled.CloseButtonContainer>

        <Styled.ScrollArea>
          <WhatsNewSlide page={currentPage} />
        </Styled.ScrollArea>

        <Styled.Footer>
          <Styled.FooterSide>
            <Button
              variant={Variants.Text}
              color={Colors.Inherit}
              size={Sizes.Medium}
              endIcon={ArrowUpRightStraight}
              onClick={handleSeeAllUpdates}
            >
              {t('whats-new.see-all-updates')}
            </Button>
          </Styled.FooterSide>

          {hasMultiplePages && (
            <Styled.Dots>
              {pages.map((_, index) => (
                <Styled.Dot key={index} $active={index === activeIndex} />
              ))}
            </Styled.Dots>
          )}

          <Styled.FooterSide $alignEnd>
            {hasMultiplePages && !isFirstPage && (
              <Button
                variant={Variants.Outlined}
                color={Colors.Primary}
                size={Sizes.Medium}
                onClick={handleBack}
              >
                {t('general.back')}
              </Button>
            )}
            <Button
              variant={Variants.Contained}
              size={Sizes.Medium}
              color={Colors.Primary}
              onClick={handleNext}
            >
              {isLastPage ? t('general.done') : t('whats-new.next')}
            </Button>
          </Styled.FooterSide>
        </Styled.Footer>
      </Styled.Content>
    </Styled.Dialog>
  );
};
